
import { useState, useEffect, useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import { metricsService, TimeFrame } from '@/services/analytics/metricsService';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Cache-busting timestamp to ensure fresh data
const cacheBuster = new Date().getTime();

export const useMetricsDashboard = () => {
  // Add separate time frame for user metrics and prompt metrics
  const [userTimeFrame, setUserTimeFrame] = useState<TimeFrame>('monthly');
  const [promptTimeFrame, setPromptTimeFrame] = useState<TimeFrame>('monthly');
  
  // Add separate date ranges
  const [userDateRange, setUserDateRange] = useState<DateRange | undefined>(undefined);
  const [promptDateRange, setPromptDateRange] = useState<DateRange | undefined>(undefined);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const [userMetrics, setUserMetrics] = useState(null);
  const [promptMetrics, setPromptMetrics] = useState(null);
  const [ratingMetrics, setRatingMetrics] = useState(null);
  const [topPrompts, setTopPrompts] = useState([]);
  
  // Convert dateRanges to start and end dates for API calls
  const userCustomRange = useMemo(() => {
    if (!userDateRange?.from) return undefined;
    
    return {
      start: userDateRange.from,
      end: userDateRange.to || userDateRange.from,
    };
  }, [userDateRange]);
  
  const promptCustomRange = useMemo(() => {
    if (!promptDateRange?.from) return undefined;
    
    return {
      start: promptDateRange.from,
      end: promptDateRange.to || promptDateRange.from,
    };
  }, [promptDateRange]);
  
  // Load real metrics data from Supabase when available
  const fetchRealMetricsData = async () => {
    try {
      console.log("Fetching real metrics data");
      console.log("User timeframe:", userTimeFrame);
      console.log("Prompt timeframe:", promptTimeFrame);
      
      if (userCustomRange) {
        console.log("User custom range:", userCustomRange.start, "to", userCustomRange.end);
      }
      
      if (promptCustomRange) {
        console.log("Prompt custom range:", promptCustomRange.start, "to", promptCustomRange.end);
      }
      
      // Get real user count from auth.users table using our secure function
      const { data: userCountData, error: userCountError } = await supabase
        .rpc('get_total_user_count');
        
      if (userCountError) {
        console.error("Error fetching user count:", userCountError);
      }
      
      const userCount = userCountData || 0;
      console.log("Total user count from auth.users:", userCount);
      
      // Get user creation dates for growth data using our new secure function
      const { data: userCreationDates, error: userCreationError } = await supabase
        .rpc('get_user_creation_dates');
      
      if (userCreationError) {
        console.error("Error fetching user creation dates:", userCreationError);
      } else {
        console.log("Retrieved creation dates for", userCreationDates?.length || 0, "users from auth.users");
      }

      // Get new users in current period using our secure function
      let newUsersInPeriod = 0;
      if (userCustomRange) {
        const { data: newUserCount, error: newUserCountError } = await supabase
          .rpc('get_new_users_in_period', {
            start_date: userCustomRange.start.toISOString(),
            end_date: userCustomRange.end.toISOString()
          });
          
        if (newUserCountError) {
          console.error("Error fetching new users count:", newUserCountError);
        } else {
          newUsersInPeriod = newUserCount;
          console.log("New users in current period:", newUsersInPeriod);
        }
      } else {
        // For standard timeframes, calculate date range and fetch new users
        const currentRange = metricsService.getDateRange(userTimeFrame);
        
        const { data: newUserCount, error: newUserCountError } = await supabase
          .rpc('get_new_users_in_period', {
            start_date: currentRange.start.toISOString(),
            end_date: currentRange.end.toISOString()
          });
          
        if (newUserCountError) {
          console.error("Error fetching new users count:", newUserCountError);
        } else {
          newUsersInPeriod = newUserCount;
          console.log("New users in current period:", newUsersInPeriod);
        }
      }
      
      // Get prompt copies data from prompt_copies table with all required fields
      const { data: copyData, error: copyError } = await supabase
        .from('prompt_copies')
        .select('prompt_id, created_at')
        .order('created_at', { ascending: true });
        
      if (copyError) {
        console.error("Error fetching prompt copy data:", copyError);
      } else {
        console.log(`Retrieved ${copyData?.length || 0} records from prompt_copies`);
        if (copyData && copyData.length > 0) {
          console.log(`First copy date: ${copyData[0].created_at}, Last copy date: ${copyData[copyData.length - 1].created_at}`);
        }
      }
      
      // Get saved prompts data from user_saved_prompts table
      const { data: savedData, error: savedError } = await supabase
        .from('user_saved_prompts')
        .select('prompt_id, saved_at')
        .order('saved_at', { ascending: true });
        
      if (savedError) {
        console.error("Error fetching saved prompts data:", savedError);
      } else {
        console.log(`Retrieved ${savedData?.length || 0} records from user_saved_prompts`);
        if (savedData && savedData.length > 0) {
          console.log(`First save date: ${savedData[0].saved_at}, Last save date: ${savedData[savedData.length - 1].saved_at}`);
        }
      }
      
      // Get collection data from collection_prompts table
      const { data: collectionsData, error: collectionsError } = await supabase
        .from('collection_prompts')
        .select('saved_prompt_id, added_at')
        .order('added_at', { ascending: true });
        
      if (collectionsError) {
        console.error("Error fetching collections data:", collectionsError);
      } else {
        console.log(`Retrieved ${collectionsData?.length || 0} records from collection_prompts`);
        if (collectionsData && collectionsData.length > 0) {
          console.log(`First collection date: ${collectionsData[0].added_at}, Last collection date: ${collectionsData[collectionsData.length - 1].added_at}`);
        }
      }
      
      // Get review data from reviews table
      const { data: reviewData, error: reviewError } = await supabase
        .from('reviews')
        .select('rating, created_at')
        .order('created_at', { ascending: true });
        
      if (reviewError) {
        console.error("Error fetching review data:", reviewError);
      }
      
      // Get feedback data from feedback table
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback')
        .select('rating, created_at, type')
        .order('created_at', { ascending: true });
        
      if (feedbackError) {
        console.error("Error fetching feedback data:", feedbackError);
      }
      
      return {
        hasRealUserData: !userCountError && userCount !== null && userCount > 0,
        userCount: userCount,
        newUsersInPeriod: newUsersInPeriod,
        userCreationDates: userCreationDates || [],
        hasCopyData: copyData && copyData.length > 0,
        copyData: copyData || [],
        hasSavedData: savedData && savedData.length > 0,
        savedData: savedData || [],
        hasCollectionsData: collectionsData && collectionsData.length > 0,
        collectionsData: collectionsData || [],
        hasReviewData: reviewData && reviewData.length > 0,
        reviewData: reviewData || [],
        hasFeedbackData: feedbackData && feedbackData.length > 0,
        feedbackData: feedbackData || []
      };
    } catch (error) {
      console.error("Error fetching real metrics data:", error);
      return { 
        hasRealUserData: false,
        newUsersInPeriod: 0,
        userCreationDates: [],
        hasCopyData: false,
        hasSavedData: false, 
        hasCollectionsData: false,
        hasReviewData: false,
        hasFeedbackData: false,
        userCount: 0,
        userData: [],
        copyData: [],
        savedData: [],
        collectionsData: [],
        reviewData: [],
        feedbackData: []
      };
    }
  };

  // Load all metrics data
  const loadMetricsData = async () => {
    try {
      setIsLoading(true);
      
      // First try to get real data from the database
      const realData = await fetchRealMetricsData();
      console.log("Real data available:", realData);
      
      // Fetch user metrics
      const userMetricsData = await metricsService.getUserMetrics(
        userTimeFrame, 
        userCustomRange, 
        realData.userCount, 
        realData.userCreationDates, 
        realData.newUsersInPeriod
      );
      
      // Fetch prompt metrics with separate timeframe
      const promptMetricsData = await metricsService.getPromptMetrics(
        promptTimeFrame, 
        promptCustomRange, 
        realData.copyData, 
        realData.savedData, 
        realData.collectionsData
      );
      
      // Fetch rating metrics with prompt timeframe
      const ratingMetricsData = await metricsService.getRatingMetrics(
        promptTimeFrame, 
        promptCustomRange, 
        realData.reviewData, 
        realData.feedbackData
      );
      
      // Fetch top prompts with prompt timeframe
      const topPromptsData = await metricsService.getTopPrompts(
        promptTimeFrame, 
        10, 
        promptCustomRange
      );
      
      // Only set data if it exists (will be null if no real data)
      if (userMetricsData) {
        console.log("Setting user metrics data");
        setUserMetrics(userMetricsData);
      } else {
        setUserMetrics(null);
      }
      
      if (promptMetricsData) {
        console.log("Setting prompt metrics data");
        setPromptMetrics(promptMetricsData);
      } else {
        setPromptMetrics(null);
      }
      
      if (ratingMetricsData) {
        console.log("Setting rating metrics data");
        setRatingMetrics(ratingMetricsData);
      } else {
        setRatingMetrics(null);
      }
      
      if (topPromptsData && topPromptsData.length > 0) {
        console.log("Setting top prompts data");
        setTopPrompts(topPromptsData);
      } else {
        setTopPrompts([]);
      }
      
    } catch (error) {
      console.error("Error loading metrics data:", error);
      toast.error("Failed to load metrics data");
      
      // Reset all metrics on error
      setUserMetrics(null);
      setPromptMetrics(null);
      setRatingMetrics(null);
      setTopPrompts([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };
  
  // Handle user time frame change
  const handleUserTimeFrameChange = (newTimeFrame: TimeFrame) => {
    setUserTimeFrame(newTimeFrame);
    if (newTimeFrame !== 'custom') {
      setUserDateRange(undefined);
    }
  };
  
  // Handle prompt time frame change
  const handlePromptTimeFrameChange = (newTimeFrame: TimeFrame) => {
    setPromptTimeFrame(newTimeFrame);
    if (newTimeFrame !== 'custom') {
      setPromptDateRange(undefined);
    }
  };
  
  // Handle data refresh
  const refreshData = () => {
    setIsRefreshing(true);
    loadMetricsData();
  };
  
  // Load data on mount and when time frames or date ranges change
  useEffect(() => {
    loadMetricsData();
  }, [userTimeFrame, userCustomRange, promptTimeFrame, promptCustomRange]);
  
  return {
    userTimeFrame,
    promptTimeFrame,
    userDateRange,
    promptDateRange,
    isLoading,
    isRefreshing,
    userMetrics,
    promptMetrics,
    ratingMetrics,
    topPrompts,
    handleUserTimeFrameChange,
    handlePromptTimeFrameChange,
    setUserDateRange,
    setPromptDateRange,
    refreshData,
  };
};
